/**
 * Extract GPU card type from instance name
 * Example: "H100PCIe.8x" → "8"
 * "MI300X.2x" → "2"
 * "Trainium.4x" → "4"
 */
export const extractGpuCardFromInstance = (instance_type) => {
  if (!instance_type) return "";

  // Match the last part after the last dot, extract the number before 'x'
  const match = instance_type.match(/\.(\d+)x$/i);
  return match ? match[1] : "";
};

/**
 * Generate a GPU name from instance type
 * Example: "H100PCIe.8x" → "H100PCIe-8x-{timestamp}"
 * Used as a default gpuname when creating new GPU
 */
export const generateGpuName = (instance_type) => {
  if (!instance_type) return "";

  const timestamp = new Date().getTime();
  return `${instance_type}-${timestamp}`;
};

/**
 * Calculate available GPU count
 * Formula: gpu_available = gpu_installed - gpu_allocated
 */
export const calculateAvailableGpu = (gpuInstalled, gpuAllocated) => {
  const installed = Number(gpuInstalled) || 0;
  const allocated = Number(gpuAllocated) || 0;
  const available = installed - allocated;

  // Ensure it doesn't go negative
  return Math.max(0, available);
};

/**
 * Validate GPU form data
 * Returns { isValid: boolean, errors: object }
 */
export const validateGpuFormData = (data) => {
  const errors = {};

  // Validate instance_type
  if (!data.instance_type || data.instance_type.trim() === "") {
    errors.instance_type = "Instance type is required";
  }

  // Validate gpuname
  if (!data.gpuname || data.gpuname.trim() === "") {
    errors.gpuname = "GPU name is required";
  }

  // Validate gpu_card
  if (!data.gpu_card || data.gpu_card.trim() === "") {
    errors.gpu_card = "GPU card is required";
  }

  // Validate gpu_type
  if (!data.gpu_type || data.gpu_type.trim() === "") {
    errors.gpu_type = "GPU type is required";
  }

  // Validate numeric fields
  const numericFields = [
    "gpu_committed",
    "gpu_installed",
    "gpu_provided",
    "gpu_allocated",
    "gpu_utilised",
  ];

  numericFields.forEach((field) => {
    const value = Number(data[field]);
    if (isNaN(value) || value < 0) {
      errors[field] = `${field} must be a non-negative number`;
    }
  });

  // Validate gpu_available is non-negative
  const available = calculateAvailableGpu(
    data.gpu_installed,
    data.gpu_allocated,
  );
  if (available < 0) {
    errors.gpu_available = "Available GPUs cannot be negative";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Format GPU data for API submission
 * Ensures all numeric fields are properly converted
 */
export const formatGpuDataForApi = (formData) => {
  return {
    instance_type: formData.instance_type.trim(),
    gpu_card: formData.gpu_card.trim(),
    gpuname: formData.gpuname.trim(),
    gpu_type: formData.gpu_type.trim(),
    gpu_committed: Number(formData.gpu_committed) || 0,
    gpu_installed: Number(formData.gpu_installed) || 0,
    gpu_provided: Number(formData.gpu_provided) || 0,
    gpu_allocated: Number(formData.gpu_allocated) || 0,
    gpu_utilised: Number(formData.gpu_utilised) || 0,
    gpu_available: calculateAvailableGpu(
      formData.gpu_installed,
      formData.gpu_allocated,
    ),
  };
};

/**
 * Calculate aggregate GPU metrics from an array of GPU records
 * Used for dashboard overall totals
 */
export const aggregateGpuMetrics = (gpuList) => {
  if (!gpuList || gpuList.length === 0) {
    return {
      total_committed: 0,
      total_installed: 0,
      total_provided: 0,
      total_allocated: 0,
      total_utilised: 0,
      total_available: 0,
    };
  }

  return {
    total_committed: gpuList.reduce(
      (sum, gpu) => sum + (Number(gpu.gpu_committed) || 0),
      0,
    ),
    total_installed: gpuList.reduce(
      (sum, gpu) => sum + (Number(gpu.gpu_installed) || 0),
      0,
    ),
    total_provided: gpuList.reduce(
      (sum, gpu) => sum + (Number(gpu.gpu_provided) || 0),
      0,
    ),
    total_allocated: gpuList.reduce(
      (sum, gpu) => sum + (Number(gpu.gpu_allocated) || 0),
      0,
    ),
    total_utilised: gpuList.reduce(
      (sum, gpu) => sum + (Number(gpu.gpu_utilised) || 0),
      0,
    ),
    total_available: gpuList.reduce(
      (sum, gpu) => sum + (Number(gpu.gpu_available) || 0),
      0,
    ),
  };
};
